import { request } from "../../utils/request";
import { resolveDisplayMediaUrl } from "../../utils/videoUrl";

export const DIGITAL_HUMAN_SCOPE = {
  PUBLIC: 0,
  MINE: 1,
  ALL: 2,
} as const;

export const DIGITAL_HUMAN_TRAIN_STATUS = {
  NOT_CLONED: "0",
  DONE: "1",
  PENDING: "2",
  FAILED: "3",
} as const;

const PAGE_SIZE_MIN = 1;
const PAGE_SIZE_MAX = 1000;

const asFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

/** 只拼后端接受的 Query，避免把 undefined / 空值编进 URL */
const buildDigitalHumanPageParams = (
  params: DigitalHuman.PageQuery
): Record<string, string | number> => {
  const pageNo = Math.max(1, Math.floor(Number(params.pageNo) || 1));
  const rawSize = Math.floor(Number(params.pageSize) || 10);
  const pageSize = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, rawSize));
  const query: Record<string, string | number> = { pageNo, pageSize };

  if (params.scope === 0 || params.scope === 1 || params.scope === 2) {
    query.scope = params.scope;
  }

  const name = params.name?.trim();
  if (name) query.name = name;

  const trainStatus =
    params.trainStatus === undefined || params.trainStatus === null
      ? ""
      : String(params.trainStatus).trim();
  if (trainStatus) query.trainStatus = trainStatus;

  return query;
};

/** 分页查询数字人 GET /video/digital-human/page */
export const getDigitalHumanPageApi = (params: DigitalHuman.PageQuery) =>
  request<DigitalHuman.PageResponse>("/video/digital-human/page", {
    method: "GET",
    params: buildDigitalHumanPageParams(params),
  });

/** 数字人克隆 POST /video/digital-human/clone（multipart：req JSON + file） */
export const cloneDigitalHumanApi = (req: DigitalHuman.CloneReq, file: File) => {
  const formData = new FormData();
  formData.append("req", new Blob([JSON.stringify(req)], { type: "application/json" }));
  formData.append("file", file);
  return request<DigitalHuman.CloneResponse>("/video/digital-human/clone", {
    method: "POST",
    body: formData,
  });
};

/** 删除本人数字人 DELETE /video/digital-human/delete */
export const deleteDigitalHumanApi = (id: number) =>
  request<DigitalHuman.DeleteResponse>("/video/digital-human/delete", {
    method: "DELETE",
    params: { id },
  });

/** 重试失败的数字人克隆 POST /video/digital-human/retry */
export const retryDigitalHumanApi = (id: number) =>
  request<DigitalHuman.RetryResponse>("/video/digital-human/retry", {
    method: "POST",
    params: { id },
  });

/** 预估数字人克隆消耗积分 GET /video/digital-human/estimate-points */
export const estimateDigitalHumanPointsApi = (durationSeconds: number) => {
  const duration = Math.floor(Number(durationSeconds));
  return request<DigitalHuman.EstimatePointsResponse>("/video/digital-human/estimate-points", {
    method: "GET",
    params: {
      durationSeconds: Number.isFinite(duration) && duration > 0 ? duration : 0,
    },
  });
};

export const digitalHumanTrainStatusText = (
  status: DigitalHuman.Item["trainStatus"]
): string => {
  switch (String(status ?? "")) {
    case DIGITAL_HUMAN_TRAIN_STATUS.DONE:
      return "已完成";
    case DIGITAL_HUMAN_TRAIN_STATUS.PENDING:
      return "克隆中";
    case DIGITAL_HUMAN_TRAIN_STATUS.FAILED:
      return "克隆失败";
    default:
      return "未克隆";
  }
};

export const digitalHumanUsable = (item: DigitalHuman.Item): boolean =>
  String(item.trainStatus) === DIGITAL_HUMAN_TRAIN_STATUS.DONE;

export const digitalHumanScopeLabel = (item: DigitalHuman.Item): string =>
  asFlag(item.isPublic) ? "公共形象" : "我的形象";

export const resolveDigitalHumanCoverUrl = (
  item: DigitalHuman.Item
): string | null => {
  const fromImage = resolveDisplayMediaUrl(item.reshapeImage || item.image);
  if (fromImage) return fromImage;
  const path = String(item.path || "").trim();
  if (/\.(avif|bmp|gif|jpe?g|png|webp)(\?|#|$)/i.test(path)) {
    return resolveDisplayMediaUrl(path);
  }
  return null;
};

const VIDEO_EXT_RE = /\.(mp4|m4v|mov|webm)(\?|#|$)/i;

/** 没有封面图时，用数字人视频首帧兜底 */
export const resolveDigitalHumanVideoUrl = (
  item: DigitalHuman.Item
): string | null => {
  const raw = String(item.video || item.modelPath || item.path || "").trim();
  if (!raw || !VIDEO_EXT_RE.test(raw)) return null;
  return resolveDisplayMediaUrl(raw);
};
